import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Check, Receipt } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * CreateInvoiceFromSelectionModal — promotes a batch of already-registered
 * product rows (from /admin/produtos or /admin/materiais) into a brand-new
 * internal FTR stock invoice.
 *
 * Use case: admin had typed products manually into the monthly ledger (or
 * has a mix of them there) and now wants to materialize those as an
 * official supplier purchase with stock tracking.
 *
 * Props:
 *  - open (bool)
 *  - rows (array of product rows from /admin/ledger) — ALREADY filtered to
 *    selected ones by the caller
 *  - onClose()
 *  - onCreated(ftr)  → called after success with the full FTR payload
 */
export default function CreateInvoiceFromSelectionModal({ open, rows, onClose, onCreated }) {
  const nav = useNavigate();
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("Convertido a partir de produtos registados");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-fill qty using the row's quantity if > 0, else 1. Pre-fill price
    // from the existing unit_price so the admin sees meaningful numbers.
    setLines((rows || []).map(r => ({
      row_id: r.id,
      product_id: r.id,
      name: r.name,
      code: r.code || "",
      category: r.category,
      unit_price: Number(r.unit_price) || 0,
      vat_rate: Number(r.vat_rate) || 22,
      quantity: Number(r.quantity) > 0 ? Number(r.quantity) : 1,
    })));
    setNotes("Convertido a partir de produtos registados");
  }, [open, rows]);

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const l of lines) {
      const sub = (Number(l.unit_price) || 0) * (Number(l.quantity) || 0);
      net += sub;
      vat += sub * (Number(l.vat_rate) || 0) / 100;
    }
    return { net: +net.toFixed(2), vat: +vat.toFixed(2), total: +(net + vat).toFixed(2) };
  }, [lines]);

  function patch(row_id, p) {
    setLines(ls => ls.map(l => l.row_id === row_id ? { ...l, ...p } : l));
  }

  async function submit() {
    const valid = lines.filter(l => Number(l.quantity) > 0 && Number(l.unit_price) >= 0);
    if (!valid.length) {
      toast.error("Defina uma quantidade > 0 para pelo menos um produto.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/stock/invoices", {
        lines: valid.map(l => ({
          product_id: l.product_id,  // reuse exact products the user selected
          name: l.name,
          code: l.code || undefined,
          category: l.category === "material" ? "material" : "produto",
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          vat_rate: Number(l.vat_rate),
        })),
        notes: notes.trim() || undefined,
      });
      toast.success(
        `${data.number} criada com ${data.lines.length} linha(s) · ${data.total.toFixed(2)} €`,
        {
          action: {
            label: "Ver fatura",
            onClick: () => nav("/admin/faturas-stock"),
          },
          duration: 8000,
        },
      );
      onCreated && onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose}
         data-testid="selection-to-invoice-modal">
      <div className="lux-card w-full max-w-3xl max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between p-4 border-b border-[#1a4640] sticky top-0 bg-[#031c18] z-10">
          <div>
            <div className="eyebrow text-[#d4af37] mb-1 flex items-center gap-2">
              <Receipt size={12} /> Criar fatura com seleção
            </div>
            <div className="font-display text-xl">
              {lines.length} produto(s) → nova FTR
            </div>
            <p className="text-xs text-[#a0b2af] mt-1 max-w-lg">
              Os produtos selecionados vão ser agrupados numa nova fatura
              interna <span className="font-mono text-[#d4af37]">FTR-NNNNNN</span>.
              Reveja as quantidades e o preço unitário (sem IVA) — o IVA é
              calculado pelo sistema. O stock de cada produto é
              atualizado automaticamente.
            </p>
          </div>
          <button onClick={onClose}
                  className="p-2 text-[#a0b2af] hover:text-[#fdfbf7]"
                  data-testid="selection-to-invoice-close">
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          {lines.length === 0 ? (
            <div className="text-center py-10 text-sm text-[#a0b2af]">
              Nenhum produto selecionado.
            </div>
          ) : (
            <>
              <div className="lux-card overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-[#a0b2af] border-b border-[#1a4640]">
                    <tr>
                      <th className="text-left py-2 px-3">Código</th>
                      <th className="text-left py-2 px-3">Produto</th>
                      <th className="text-left py-2 px-3">Tipo</th>
                      <th className="text-right py-2 px-3">Preço uni. €</th>
                      <th className="text-right py-2 px-3">Qtd</th>
                      <th className="text-right py-2 px-3">IVA %</th>
                      <th className="text-right py-2 px-3">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1a4640]/40">
                    {lines.map(l => {
                      const sub = (Number(l.unit_price) || 0) * (Number(l.quantity) || 0);
                      const total = sub * (1 + (Number(l.vat_rate) || 0) / 100);
                      return (
                        <tr key={l.row_id} data-testid={`selection-line-${l.row_id}`}>
                          <td className="py-2 px-3 font-mono text-[#d4af37] text-xs">{l.code || "—"}</td>
                          <td className="py-2 px-3 text-[#fdfbf7]">{l.name}</td>
                          <td className="py-2 px-3 text-xs text-[#a0b2af]">
                            {l.category === "material" ? "Material" : "Produto"}
                          </td>
                          <td className="py-2 px-3">
                            <input type="number" step="0.01" min="0"
                                   value={l.unit_price}
                                   onChange={e => patch(l.row_id, { unit_price: e.target.value })}
                                   className="w-24 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1 text-right text-sm tabular-nums outline-none"
                                   data-testid={`selection-price-${l.row_id}`} />
                          </td>
                          <td className="py-2 px-3">
                            <input type="number" step="0.01" min="0.01"
                                   value={l.quantity}
                                   onChange={e => patch(l.row_id, { quantity: e.target.value })}
                                   className="w-20 bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-2 py-1 text-right text-sm tabular-nums outline-none"
                                   data-testid={`selection-qty-${l.row_id}`} />
                          </td>
                          <td className="py-2 px-3 text-right text-xs text-[#a0b2af] tabular-nums">{l.vat_rate}%</td>
                          <td className="py-2 px-3 text-right tabular-nums text-[#d4af37]">
                            {total.toFixed(2)} €
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Notas (opcional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                          className="w-full bg-transparent border border-[#1a4640] focus:border-[#d4af37] rounded-sm px-3 py-2 text-sm outline-none mt-1"
                          rows={2}
                          data-testid="selection-notes" />
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#1a4640]">
                <div className="text-sm">
                  <span className="text-[#a0b2af]">Líquido </span>
                  <span className="tabular-nums text-[#fdfbf7]">{totals.net.toFixed(2)} €</span>
                  <span className="text-[#a0b2af]"> + IVA </span>
                  <span className="tabular-nums text-[#fdfbf7]">{totals.vat.toFixed(2)} €</span>
                  <span className="text-[#a0b2af]"> = </span>
                  <span className="font-display text-xl text-[#d4af37]" data-testid="selection-total">
                    {totals.total.toFixed(2)} €
                  </span>
                </div>
                <button onClick={submit}
                        disabled={saving || !lines.length}
                        className="btn-gold px-6 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50"
                        data-testid="selection-submit">
                  {saving ? <><Loader2 size={14} className="animate-spin" /> A criar…</>
                          : <><Check size={14} /> Criar fatura</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
